// ~/modules/Songs/SongsService.ts
import dayjs from 'dayjs';
import { Song, SongPreview } from '~/interfaces';
import convertTxtToSong from '~/modules/Songs/utils/convertTxtToSong';
import { generatePlayerChangesForTrack } from '~/modules/Songs/utils/generatePlayerChanges';
import getSongId from '~/modules/Songs/utils/getSongId';
import mergeTracks from '~/modules/Songs/utils/mergeTracks';
import { lastVisit } from '~/modules/Stats/lastVisit';
import storage from '~/modules/utils/storage';
import { getSongPreview } from './utils';

let store: Promise<LocalForage | typeof storage.memory> | null = null;

async function getStorage() {
  if (!store) {
    if ('localStorage' in globalThis) {
      try {
        store = import('localforage').then(({ default: localForage }) =>
          localForage.createInstance({ name: 'songs_v2' }),
        );
      } catch (e) {
        console.error(e);
        store = Promise.resolve(storage.memory);
      }
    } else {
      store = Promise.resolve(storage.memory);
    }
  }
  return store;
}

const DELETED_SONGS_KEY = 'DELETED_SONGS_V2';
const KARAOKE_INDEX_PATH = '/karaoke/index.json';

function ensureValidBpm(bpm: unknown, fallback = 120) {
  const n = Number(bpm);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.round(n);
}

function ensureValidNumber(n: unknown, fallback = 0) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return x;
}

function normalizeLanguage(language: Song['language']): Song['language'] {
  return Array.isArray(language) ? language : [language];
}

type SongPreviewWithScore = SongPreview & {
  scoreId?: string;
  songTitle?: string;
  songArtist?: string;
};

class SongsService {
  private defaultIndex: SongPreviewWithScore[] | null = null;
  private finalIndex: SongPreviewWithScore[] | null = null;
  private indexWithDeletedSongs: SongPreviewWithScore[] | null = null;

  public store = async (song: Song, reloadIndex = true) => {
    await (
      await getStorage()
    )?.setItem(this.generateSongFile(song), {
      ...song,
      lastUpdate: new Date().toISOString(),
    });

    if (reloadIndex) {
      await this.reloadIndex();
    }
  };

  public isOverridden = async (songId: string) => {
    const localSong = await this.getLocal(songId);
    return !!localSong;
  };

  public isBuiltIn = (songId: string) => {
    return this.defaultIndex?.some((song) => song.id === songId) ?? false;
  };

  private findIndexSong = (songId: string): SongPreviewWithScore | undefined => {
    return (
      this.finalIndex?.find((song) => song.id === songId) ||
      this.indexWithDeletedSongs?.find((song) => song.id === songId) ||
      this.defaultIndex?.find((song) => song.id === songId)
    );
  };

  private hydrateSong = (
    parsedSong: Song,
    songId: string,
    karaokeEntry?: SongPreviewWithScore,
    options?: {
      scoreId?: string;
      isTemplateFallback?: boolean;
      forceSafeTemplateValues?: boolean;
    },
  ): Song => {
    const scoreId = options?.scoreId;
    const isTemplateFallback = options?.isTemplateFallback ?? false;
    const forceSafeTemplateValues = options?.forceSafeTemplateValues ?? false;

    const bpm = forceSafeTemplateValues
      ? ensureValidBpm(parsedSong.bpm, 120)
      : ensureValidBpm(parsedSong.bpm, 120);

    const hydrated: Song = {
      ...parsedSong,
      id: songId,
      title: karaokeEntry?.title ?? parsedSong.title,
      artist: karaokeEntry?.artist ?? parsedSong.artist,
      video: karaokeEntry?.video ?? parsedSong.video,
      local: false,
      bpm,
      videoGap: ensureValidNumber(karaokeEntry?.videoGap ?? parsedSong.videoGap, 0),
      gap: ensureValidNumber(karaokeEntry?.gap ?? parsedSong.gap, 0) as Song['gap'],
      trackOffsetsMs: karaokeEntry?.trackOffsetsMs ?? parsedSong.trackOffsetsMs,
      previewStart: karaokeEntry?.previewStart ?? parsedSong.previewStart,
      previewEnd: karaokeEntry?.previewEnd ?? parsedSong.previewEnd,
      volume: ensureValidNumber(karaokeEntry?.volume ?? parsedSong.volume, 1),
      manualVolume: ensureValidNumber(karaokeEntry?.manualVolume ?? parsedSong.manualVolume, 1),
      language: normalizeLanguage(karaokeEntry?.language ?? parsedSong.language),
      meta: {
        ...(parsedSong.meta ?? {}),
        ...(scoreId ? { scoreId } : {}),
        originalSongId: songId,
        isTemplateFallback,
      },
    };

    console.log('🎤 HYDRATE DEBUG', {
      songId,
      scoreId,
      karaokeGap: karaokeEntry?.gap,
      parsedGap: parsedSong.gap,
      finalGap: ensureValidNumber(karaokeEntry?.gap ?? parsedSong.gap, 0),
      karaokeVideoGap: karaokeEntry?.videoGap,
      parsedVideoGap: parsedSong.videoGap,
      finalVideoGap: ensureValidNumber(karaokeEntry?.videoGap ?? parsedSong.videoGap, 0),
    });
    return {
      ...hydrated,
      mergedTrack: mergeTracks(hydrated.tracks, hydrated),
      tracks: hydrated.tracks.map((track) => ({
        ...track,
        changes: generatePlayerChangesForTrack(track, hydrated),
      })),
    };
  };

  private loadTemplateSong = async (songId: string, karaokeEntry?: SongPreviewWithScore, scoreId?: string) => {
    const templateRes = await fetch(`/songs/template.txt`);
    if (!templateRes.ok) {
      throw new Error(`Missing template (/songs/template.txt).`);
    }

    const templateTxt = await templateRes.text();
    const templateSong = convertTxtToSong(templateTxt);

    return this.hydrateSong(templateSong, songId, karaokeEntry, {
      scoreId,
      isTemplateFallback: true,
      forceSafeTemplateValues: true,
    });
  };

  /**
   * Lógica final:
   * - override local -> canción local
   * - si hay scoreId y existe /songs/{scoreId}.txt -> usa ese txt
   * - si no hay scoreId o no existe -> usa /songs/template.txt
   *
   * IMPORTANTE:
   * - el vídeo SIEMPRE lo manda karaoke/index.json
   * - el txt solo aporta scoring/timing
   */
  public get = async (songId: string): Promise<Song> => {
    const localSong = await this.getLocal(songId);
    if (localSong) return { ...localSong, local: true };

    const karaokeEntry = this.findIndexSong(songId);
    const scoreId = karaokeEntry?.scoreId?.trim();

    if (!scoreId) {
      return this.loadTemplateSong(songId, karaokeEntry);
    }

    const scoreRes = await fetch(`/songs/${scoreId}.txt`);

    if (!scoreRes.ok) {
      return this.loadTemplateSong(songId, karaokeEntry, scoreId);
    }

    const txt = await scoreRes.text();
    const parsed = convertTxtToSong(txt);

    return this.hydrateSong(parsed, songId, karaokeEntry, {
      scoreId,
      isTemplateFallback: false,
    });
  };

  public getIndex = async (includeDeleted = false): Promise<SongPreview[]> => {
    if (this.finalIndex === null) {
      await this.reloadIndex();
    }
    return includeDeleted ? this.indexWithDeletedSongs! : this.finalIndex!;
  };

  public getCurrentIndex = () => this.finalIndex;

  public getDeletedSongsList = async () => {
    const list = await (await getStorage())?.getItem<string[]>(DELETED_SONGS_KEY);
    return list ?? [];
  };

  public generateSongFile = (song: Pick<Song | SongPreview, 'artist' | 'title'> & { id?: string }) => getSongId(song);

  public reloadIndex = async () => {
    const [defaultIndex, storageIndex, deletedSongs] = await Promise.all([
      fetch(KARAOKE_INDEX_PATH).then((response) => response.json() as Promise<SongPreviewWithScore[]>),
      this.getLocalIndex(),
      this.getDeletedSongsList(),
    ]);

    this.defaultIndex = defaultIndex;
    const lastVisitDate = dayjs(lastVisit);

    const storageIndexWithUpdatedSongs = storageIndex.filter((song) => {
      const defaultSong = defaultIndex.find((localSong) => localSong.id === song.id);
      if (!defaultSong) return true;
      return dayjs(song.lastUpdate ?? 0).isAfter(dayjs(defaultSong.lastUpdate ?? 0));
    });

    const localSongs = storageIndexWithUpdatedSongs.map((song) => song.id);

    storageIndex.forEach(async (song) => {
      if (!localSongs.includes(song.id)) {
        (await getStorage()).removeItem(song.id);
      }
    });

    this.indexWithDeletedSongs = [
      ...storageIndexWithUpdatedSongs,
      ...defaultIndex.filter((song) => !localSongs.includes(this.generateSongFile(song))),
    ].map((song) => ({
      ...song,
      isBuiltIn: this.isBuiltIn(song.id),
      isNew: song.lastUpdate ? dayjs(song.lastUpdate).isAfter(lastVisitDate) : false,
      isDeleted: deletedSongs?.includes(this.generateSongFile(song)),
    }));

    this.indexWithDeletedSongs.sort((a, b) => {
      const artistCompare = (a.artist || '').localeCompare(b.artist || '', undefined, {
        sensitivity: 'base',
      });

      if (artistCompare !== 0) return artistCompare;

      return (a.title || '').localeCompare(b.title || '', undefined, {
        sensitivity: 'base',
      });
    });

    this.finalIndex = this.indexWithDeletedSongs.filter((song) => !song.isDeleted);
  };

  public deleteSong = async (songId: string) => {
    await (await getStorage()).removeItem(songId);
    return this.reloadIndex();
  };

  public softDeleteSong = async (songId: string) => {
    const deletedItems = await this.getDeletedSongsList();
    await (await getStorage())?.setItem(DELETED_SONGS_KEY, [...new Set([...deletedItems, songId])]);
    return this.reloadIndex();
  };

  public restoreSong = async (songId: string) => {
    const deletedItems = await this.getDeletedSongsList();
    await (await getStorage())?.setItem(
      DELETED_SONGS_KEY,
      deletedItems.filter((item) => item !== songId),
    );
    return this.reloadIndex();
  };

  private getLocal = async (songId: string) => {
    const song = await (await getStorage())?.getItem<Song>(decodeURIComponent(songId));
    if (song && !!song.tracks) {
      return {
        ...song,
        language: normalizeLanguage(song.language),
        mergedTrack: mergeTracks(song.tracks, song),
        tracks: song.tracks.map((track) => ({
          ...track,
          changes: generatePlayerChangesForTrack(track, song),
        })),
      };
    }
    return null;
  };

  public getLocalIndex = async () => {
    const allSongs = await Promise.all((await this.getKeys()).map(this.getLocal));

    return allSongs
      .filter((song): song is Song => song !== null)
      .map((song) => {
        try {
          return getSongPreview(song, { local: true });
        } catch (e) {
          console.error(e);
          return null;
        }
      })
      .filter((song): song is SongPreview => song !== null);
  };

  private getKeys = async () => {
    const specialKeys = [DELETED_SONGS_KEY];
    return (await (await getStorage()).keys()).filter((key) => !specialKeys.includes(key));
  };
}

export default new SongsService();